import { OverlayContainer } from '@angular/cdk/overlay';
import { ElementRef, Injectable, OnDestroy, Renderer2 } from '@angular/core';

import {ApBaseClass} from 'webcommon/shared';

import {ApActiveModal} from './ApActiveModal';

// This class contains common logic that needs to be run inside of ngx-bootstrap modals.
// It previously existed in class ApBaseModalComponent, but was moved here to allow it to be shared
// with ApLockingBaseModalComponent (previously FndBaseModalComponent), etc.
// This class is not actually injectable because FakeHideHandlerInjectionToken doesn't have a service registered for it.
// The decorator is required because it relies on the OnDestroy lifecycle hook and Angular requires a decorator to implement the hook.
@Injectable()
export class ApModalHelper<T> extends ApBaseClass implements OnDestroy {
  constructor(
    private activeModal: ApActiveModal<T>,

    private renderer2: Renderer2,
    private elementRef: ElementRef<HTMLElement>,
    private overlayContainer: OverlayContainer,
  ) {
    super();
    this.activeModal.markReadyToHide();
  }

  private makeCompatibleWithBootstrapModalOnInit() {
    // mimic the behavior of ngx-bootstrap config for backwards compatibility,
    // so that all the current modals don't have to be refactored to work/look correctly.

    // https://github.com/valor-software/ngx-bootstrap/blob/v9.0.0/src/modal/modal-container.component.ts#L20-L37
    // TODO: We should probably take this out eventually to go more off of material standards
    // by using this: https://v15.material.angular.io/components/dialog/overview
    const element = this.elementRef.nativeElement;
    const parent1 = element.parentElement;
    this.renderer2.addClass(parent1, 'modal-content');
    this.renderer2.addClass(parent1, 'ap-cdk-bootstrap-modal-override');

    const parent2 = parent1?.parentElement;
    this.renderer2.addClass(parent2, 'modal-dialog');
    this.renderer2.addClass(parent2, 'ap-cdk-bootstrap-modal-override');

    const parent3 = parent2?.parentElement;
    this.renderer2.addClass(parent3, 'modal');
    this.renderer2.addClass(parent3, 'ap-cdk-bootstrap-modal-override');
  }

  private makeCompatibleWithBootstrapModalOnDestroy() {
    // revert changes that were made outside of this modal element and its wrapper
    this.overlayContainer.getContainerElement().classList.remove('modal-container-top');
  }

  // call from ngOnInit in component
  onInit(): void {
    this.makeCompatibleWithBootstrapModalOnInit();
  }

  modalData<TData>(): TData {
    return this.activeModal.dialogRef.config.data;
  }

  // resolve the promise because the user is done and there is a successful result
  emitAndClose(result: T | PromiseLike<T>): void {
    return this.activeModal.emitAndClose(result);
  }
  // reject the promise
  dismiss(reason?: any): void {
    return this.activeModal.dismiss(reason);
  }

  // resolve the promise because there is a successful result, but don't close the modal
  // call when closing of modal is already done elsewhere
  // THIS SHOULD ONLY BE CALLED IN VERY RARE CASES
  emitWithoutClose(result: T | PromiseLike<T>): void {
    return this.activeModal.emitWithoutClose(result);
  }
  // reject the promise, but don't close the modal
  // call when closing of modal is already done elsewhere
  // THIS SHOULD ONLY BE CALLED IN VERY RARE CASES
  dismissWithoutClose(reason?: any): void {
    return this.activeModal.dismissWithoutClose(reason);
  }

  override ngOnDestroy(): void {
    this.makeCompatibleWithBootstrapModalOnDestroy();
    super.ngOnDestroy();
  }
}
